import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../context/LanguageContext';

const { 
  FiLayers, FiCompass, FiGrid, FiLink, FiShare2, FiCamera, 
  FiMessageSquare, FiGlobe, FiDollarSign, FiShield, FiArrowRight, FiCheckCircle
} = FiIcons;

const EcosystemTab = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activePhase, setActivePhase] = useState('all');

  const solutionTabs = ['pilot', 'nima', 'unity', 'bifrost', 'memories', 'scotty', 'avatars', 'pollio'];

  const handleProductClick = (prodId) => {
    if (solutionTabs.includes(prodId)) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', 'solutions');
      newParams.set('tab', prodId);
      setSearchParams(newParams);
      window.scrollTo({ top: 300, behavior: 'smooth' });
    }
  };

  const phases = [
    {
      id: "p1",
      phase: t('solutionsPage.ecosystem.phases.p1Phase') || "Phase 01",
      title: t('solutionsPage.ecosystem.phases.p1Title') || "Planning & Orchestration",
      desc: t('solutionsPage.ecosystem.phases.p1Desc') || "Setting the foundation with powerful management and automation event tools.",
      color: "from-indigo-500 to-blue-600",
      border: "border-indigo-200",
      bg: "bg-indigo-50/50",
      items: [
        { id: "pilot", name: "Pilot", detail: t('solutionsPage.ecosystem.items.pilot') || "Central command for logistics.", icon: FiCompass, color: "text-indigo-600" },
        { id: "nima", name: "Nima", detail: t('solutionsPage.ecosystem.items.nima') || "Automate 1,000+ app workflows.", icon: FiGrid, color: "text-cyan-600" }
      ]
    },
    {
      id: "p2",
      phase: t('solutionsPage.ecosystem.phases.p2Phase') || "Phase 02",
      title: t('solutionsPage.ecosystem.phases.p2Title') || "Amplification & Reach",
      desc: t('solutionsPage.ecosystem.phases.p2Desc') || "Broadcasting your event across the social web to maximize visibility.",
      color: "from-emerald-500 to-teal-600",
      border: "border-emerald-200",
      bg: "bg-emerald-50/50",
      items: [
        { id: "unity", name: "Unity", detail: t('solutionsPage.ecosystem.items.unity') || "Conversion-first event embedding.", icon: FiLink, color: "text-emerald-600" },
        { id: "bifrost", name: "Bifrost", detail: t('solutionsPage.ecosystem.items.bifrost') || "13-network social distribution.", icon: FiShare2, color: "text-pink-600" }
      ]
    },
    {
      id: "p3",
      phase: t('solutionsPage.ecosystem.phases.p3Phase') || "Phase 03",
      title: t('solutionsPage.ecosystem.phases.p3Title') || "Immersion & Community",
      desc: t('solutionsPage.ecosystem.phases.p3Desc') || "Creating unforgettable experiences through AI, virtual worlds, and shared media.",
      color: "from-purple-500 to-violet-600",
      border: "border-purple-200",
      bg: "bg-purple-50/50",
      items: [
        { id: "memories", name: "Memories", detail: t('solutionsPage.ecosystem.items.memories') || "Private AI-powered social galleries.", icon: FiCamera, color: "text-purple-600" },
        { id: "scotty", name: "Scotty", detail: t('solutionsPage.ecosystem.items.scotty') || "24/7 AI guest concierge.", icon: FiMessageSquare, color: "text-violet-600" },
        { id: "avatars", name: "Avatars", detail: t('solutionsPage.ecosystem.items.avatars') || "Immersive virtual venues.", icon: FiGlobe, color: "text-blue-600" }
      ]
    },
    {
      id: "p4",
      phase: t('solutionsPage.ecosystem.phases.p4Phase') || "Phase 04",
      title: t('solutionsPage.ecosystem.phases.p4Title') || "Enterprise Scaling",
      desc: t('solutionsPage.ecosystem.phases.p4Desc') || "Powering global commerce and white-label brand authority.",
      color: "from-slate-700 to-slate-900",
      border: "border-slate-200",
      bg: "bg-slate-50/50",
      items: [
        { id: "commerce", name: "E-commerce", detail: t('solutionsPage.ecosystem.items.commerce') || "135+ currencies, 6 providers.", icon: FiDollarSign, color: "text-slate-700" },
        { id: "whitelabel", name: "Whitelabel", detail: t('solutionsPage.ecosystem.items.whitelabel') || "Your domain, your brand.", icon: FiShield, color: "text-slate-800" }
      ]
    }
  ];

  const displayedPhases = activePhase === 'all' 
    ? phases 
    : phases.filter(p => p.id === activePhase);

  return (
    <div className="space-y-12 py-2">
      {/* Hero Header */}
      <div className="text-center max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-indigo-700 text-xs font-semibold uppercase tracking-wider shadow-sm inner-glow">
          <SafeIcon icon={FiLayers} className="w-3.5 h-3.5 text-indigo-600 animate-pulse" />
          <span>{t('solutionsPage.ecosystem.badge') || "The Integrated Suite For Success"}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          {t('solutionsPage.ecosystem.title') || "Full Event Ecosystem"}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          {t('solutionsPage.ecosystem.subtitle') || "The Event Lifecycle of Success: Setting the foundation, broadcasting reach, creating immersive community, and powering enterprise scaling."}
        </p>
      </div>

      {/* Interactive Phase Filter Buttons */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          onClick={() => setActivePhase('all')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activePhase === 'all'
              ? 'bg-slate-900 text-white shadow-md'
              : 'bg-white/80 text-slate-600 border border-slate-200 hover:bg-white'
          }`}
        >
          {t('solutionsPage.ecosystem.allPhases') || "All 4 Lifecycle Phases"}
        </button>
        {phases.map((p) => (
          <button
            key={p.id}
            onClick={() => setActivePhase(p.id)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              activePhase === p.id
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-white/80 text-slate-600 border border-slate-200 hover:bg-white'
            }`}
          >
            {p.phase}: {p.title}
          </button>
        ))}
      </div>

      {/* The 4 Event Lifecycle Phases */}
      <div className="space-y-6">
        {displayedPhases.map((phaseItem, index) => (
          <motion.div
            key={phaseItem.phase}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className={`glass-panel rounded-[24px] p-6 md:p-8 border ${phaseItem.border} shadow-lg shadow-slate-100/60 relative overflow-hidden`}
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-slate-200/70">
              <div className="space-y-1.5">
                <span className="px-3 py-1 rounded-full text-[11px] font-mono font-extrabold uppercase tracking-wider bg-white border border-slate-200 text-slate-700 shadow-xs">
                  {phaseItem.phase}
                </span>
                <h3 className="text-xl font-extrabold text-slate-900 tracking-tight">{phaseItem.title}</h3>
                <p className="text-sm text-slate-600 font-medium">{phaseItem.desc}</p>
              </div>
            </div>

            {/* Products in Phase */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-6">
              {phaseItem.items.map((prod) => {
                const isClickable = solutionTabs.includes(prod.id);
                return (
                  <div
                    key={prod.id}
                    onClick={() => isClickable && handleProductClick(prod.id)}
                    className={`p-4 rounded-2xl bg-white/90 border border-slate-200/80 shadow-xs transition-all flex items-start gap-3.5 ${
                      isClickable 
                        ? 'hover:border-indigo-300 hover:bg-indigo-50/20 hover:shadow-md cursor-pointer group' 
                        : ''
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 shadow-xs ${prod.color} ${isClickable ? 'group-hover:scale-105 transition-transform' : ''}`}>
                      <SafeIcon icon={prod.icon} className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className={`text-sm font-bold text-slate-900 ${isClickable ? 'group-hover:text-indigo-600 transition-colors' : ''}`}>{prod.name}</h4>
                        {isClickable && (
                          <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 leading-relaxed font-normal">{prod.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default EcosystemTab;
